import { withI18n } from '@lingui/react'
import classNames from 'classnames'
import { arrayOf, bool, number, oneOf, shape } from 'prop-types'
import { compose } from 'redux'

import {
  AVAILABLE_PRESCRIPTION_TYPES,
  AVAILABLE_PRODUCT_TEMPLATES,
  PRESCRIPTION_TYPE_NO_PRESCRIPTION,
  PRESCRIPTION_TYPE_TO_PRICE_TYPE,
  PRICE_TYPES,
} from 'shared/consts/productTemplates'
import { TENANT_IT } from 'shared/consts/tenants'
import { getPriceForType } from 'shared/utils/productTemplateUtils'
import { shouldUseOTCMode } from 'views/components/organisms/ProductVariants/helpers'
import {
  DiscountBadge,
  ListPrice,
  RetailPrice,
  VariantColumn,
} from 'views/components/organisms/ProductVariants/ProductVariant/ProductVariantComponents'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { i18nType, publicRuntimeConfigType } from 'views/utils/propTypes'

const ProductVariantAvailableDetails = ({
  discount,
  i18n,
  isOutOfStock,
  prescriptionType,
  prices,
  productTemplate,
  publicRuntimeConfig: {
    publicConfig: { tenant },
  },
  withExtraMargin,
  isMasterPage,
  lowestPrice,
}) => {
  const useOTCMode = shouldUseOTCMode({ prescriptionType, productTemplate })
  const listPriceAmount = getPriceForType(prices, PRICE_TYPES.LIST)
  const realPriceAmount = getPriceForType(
    prices,
    PRESCRIPTION_TYPE_TO_PRICE_TYPE[prescriptionType]
  )
  const bonus = getPriceForType(prices, prescriptionType)
  const headingClass =
    Math.floor(listPriceAmount?.amount) >= 1000 ? 'a-h3' : 'a-h2'
  const listPriceClass = `${headingClass} u-no-margin u-color--grey u-font-weight--normal o-ProductVariantAvailableDetails--price`
  const retailPriceClass = classNames(
    `${headingClass} u-no-margin u-padding--left u-text-align--right o-ProductVariantAvailableDetails--price`,
    {
      'u-margin-large--left@medium': tenant === TENANT_IT,
      'u-color--red': useOTCMode && !isOutOfStock,
      'u-color--grey': isOutOfStock,
      'u-margin--left': withExtraMargin,
    }
  )
  const listPriceProps = {
    amount:
      listPriceAmount.amount && listPriceAmount.amount > realPriceAmount.amount
        ? listPriceAmount.amount
        : null,
    noLineThrough:
      Boolean(listPriceAmount) &&
      Boolean(realPriceAmount) &&
      listPriceAmount.amount === realPriceAmount.amount,
  }
  const retailPriceProps = {
    amount: realPriceAmount.amount,
    prescriptionType,
  }
  const discountBadgeProps = {
    productTemplate,
    prescriptionType,
    useOTCMode,
    isOutOfStock,
    bonus: bonus ? bonus.bonus : 0,
    discount,
  }

  if (isOutOfStock && !realPriceAmount.amount) {
    return (
      <VariantColumn
        className={classNames(
          listPriceClass,
          'l-col--7-of-12 u-text-align--center'
        )}
        labeledBy="variants_title_out_of_stock"
      >
        {i18n._('general.label.outOfStock')}
      </VariantColumn>
    )
  }

  return (
    <>
      {/* on medium bp this one shows list price column separately
      and it's not rendered below medium bp. Represents first column - list price or AVP/UVP */}
      <VariantColumn
        className={`${listPriceClass} u-padding--left u-hidden@pre-medium-small-tiny l-col--2-of-12 u-no-wrap o-ProductVariantAvailableDetails--old-price o-ProductVariantAvailableDetails--mobile-hidden`}
        labeledBy="variants_title_avp"
      >
        <ListPrice {...listPriceProps} />
      </VariantColumn>

      {/* this column isn't rendered after medium bp. This one is the collapsing
      column which groups retail and list price together for smaller screens. Visible only on smaller screens. */}
      <VariantColumn
        className={`${retailPriceClass} l-col--3-of-12@medium u-hidden@pre-medium`}
        labeledBy="variants_title_price"
      >
        <VariantColumn
          className={`${listPriceClass} u-text-align--right u-no-wrap`}
          labeledBy="variants_title_avp"
        >
          <ListPrice {...listPriceProps} />
        </VariantColumn>

        <RetailPrice
          {...retailPriceProps}
          isMasterPage={isMasterPage}
          lowestPrice={lowestPrice}
        />
      </VariantColumn>

      {/* this one represents retail price / real price and is rendered only after medium bp. Hidden on mobile */}
      <VariantColumn
        className={`${retailPriceClass} l-col--3-of-12@medium u-hidden@pre-medium-small-tiny o-ProductVariantAvailableDetails--mobile-hidden`}
        labeledBy="variants_title_price"
      >
        <RetailPrice
          {...retailPriceProps}
          isMasterPage={isMasterPage}
          lowestPrice={lowestPrice}
        />
      </VariantColumn>

      {/* this column is always rendered. Represents third column (discount/bonus) in variant row */}
      <VariantColumn
        className="a-h5 u-no-margin u-text-align--right u-padding--left u-no-wrap l-col--3-of-12@pre-medium l-col--4-of-12 o-ProductVariantAvailableDetails--discount"
        labeledBy="variants_title_saving"
        prescriptionTypeData
      >
        <DiscountBadge {...discountBadgeProps} />
      </VariantColumn>
    </>
  )
}

ProductVariantAvailableDetails.propTypes = {
  discount: number,
  i18n: i18nType.isRequired,
  isMarketplaceProduct: bool,
  isOutOfStock: bool,
  isOutOfTrade: bool,
  prescriptionType: oneOf(AVAILABLE_PRESCRIPTION_TYPES.ALL),
  prices: arrayOf(shape({})).isRequired,
  productTemplate: oneOf(AVAILABLE_PRODUCT_TEMPLATES).isRequired,
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  useOTCMode: bool,
  withExtraMargin: bool.isRequired,
  isMasterPage: bool,
  lowestPrice: number,
}

ProductVariantAvailableDetails.defaultProps = {
  discount: 0,
  isMarketplaceProduct: false,
  isOutOfStock: false,
  isOutOfTrade: false,
  prescriptionType: PRESCRIPTION_TYPE_NO_PRESCRIPTION,
  useOTCMode: false,
  isMasterPage: false,
}

export default compose(
  withShopAndRuntimeConfig,
  withI18n()
)(ProductVariantAvailableDetails)
